import * as i from 'io-ts';
import axios from 'axios';
import { ServiceConfig } from '../../../serviceConfig';
import * as P from '../particle';
import { Pose } from '../../../pose';
import * as TE from 'fp-ts/TaskEither';
import * as Err from '@fixtuur/error-context';
import { pipe } from 'fp-ts/lib/function';
import { Slot } from './slot';

export * as Slot from './slot';

export const Attachments = i.type({ tag: i.string, slots: i.array(Slot), originDelta: Pose });

export type Attachments = i.TypeOf<typeof Attachments>;

/** Construct the URL to fetch the attachment data for a given Particle. */
export const urlForParticle = (serviceUrls: ServiceConfig, particle: P.Particle) =>
    `${serviceUrls.models}/${P.id(particle)}.v2.json`;

export enum FromParticleErrorMessage {
    NoAttachments = 'No attachments found for particle',
    AttachmentsMalformed = 'The downloaded attachments were malformed',
    Unknown = 'Unknown error fetching attachments',
}

export type FromParticleError = Err.ErrorContext<FromParticleErrorMessage>;

export const fromParticle = (
    serviceUrls: ServiceConfig,
    particle: P.Particle,
): TE.TaskEither<FromParticleError, Attachments> => {
    return pipe(
        TE.tryCatch(
            () =>
                axios.get(urlForParticle(serviceUrls, particle), {
                    responseType: 'json',
                }),
            err =>
                pipe(
                    err,
                    Err.fromUnknownWithContext({ particleId: P.id(particle) }),
                    Err.swap(FromParticleErrorMessage.Unknown),
                ),
        ),
        TE.chain(response =>
            pipe(
                response.data,
                Attachments.decode,
                TE.fromEither,
                TE.mapLeft(err =>
                    pipe(
                        err,
                        Err.fromUnknownWithContext({ particleId: P.id(particle) }),
                        Err.swap<unknown, FromParticleErrorMessage>(
                            FromParticleErrorMessage.AttachmentsMalformed,
                        ),
                    ),
                ),
            ),
        ),
    );
};
